import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Leaf, CheckCircle2 } from "lucide-react";
import { api, getErrorMessage } from "../api";
import { useNurserySettings } from "../SettingsContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import CountryCombobox from "../components/CountryCombobox";
import FieldLabel, { RequiredLegend } from "../components/FieldLabel";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { Toaster } from "../components/ui/sonner";
import { toast } from "sonner";

export default function Register() {
  const navigate = useNavigate();
  const { nurseryName, logoUrl } = useNurserySettings();
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", password: "", phone: "",
    country_code: "", company: "", vat_id: "",
    street_address: "", address_line2: "", city: "", postal_code: "",
    import_license: false, import_license_number: "",
    accept_terms: false,
  });

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const setMany = (patch) => setForm((s) => ({ ...s, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.country_code) { toast.error("Country is required"); return; }
    if (!form.company?.trim()) { toast.error("Company name is required"); return; }
    if (!form.vat_id?.trim()) { toast.error("VAT / Tax ID is required"); return; }
    if (!form.accept_terms) { toast.error("Please accept the General Sales and Delivery Terms"); return; }
    setBusy(true);
    try {
      await api.post("/auth/register", {
        ...form,
        country_code: form.country_code || null,
        import_license_number: form.import_license ? form.import_license_number : null,
      });
      setOk(true);
    } catch (err) {
      toast.error(getErrorMessage(err, "Registration failed"));
    } finally {
      setBusy(false);
    }
  };

  if (ok) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#F9F9F8] p-6">
        <div className="max-w-md w-full bg-white border border-[#E2E4E0] rounded-lg p-8 text-center" data-testid="register-success">
          <CheckCircle2 size={36} className="mx-auto text-[#40916C]" />
          <h2 className="db-h text-2xl font-bold mt-3">Registration received</h2>
          <p className="text-sm text-[#5C665A] mt-2">
            Your account is <span className="font-medium">pending admin approval</span>. We&rsquo;ll notify you once it&rsquo;s active.
          </p>
          <Button
            onClick={() => navigate("/login")}
            data-testid="register-back-to-login"
            className="mt-6 bg-[#2D6A4F] hover:bg-[#1B4332] text-white"
          >
            Back to sign in
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F8]">
      <div className="max-w-3xl mx-auto p-6 sm:p-10">
        <Link to="/login" className="inline-flex items-center gap-2 mb-6" data-testid="link-back-login">
          <span className="w-8 h-8 rounded-md bg-[#2D6A4F] text-white grid place-items-center overflow-hidden" data-testid="register-logo">
            {logoUrl ? <img src={logoUrl} alt="" className="w-full h-full object-contain" /> : <Leaf size={16} />}
          </span>
          <div className="db-h text-sm font-bold truncate max-w-[220px]" data-testid="register-name">{nurseryName}</div>
        </Link>
        <div className="bg-white border border-[#E2E4E0] rounded-lg p-6 sm:p-8">
          <div className="db-h flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Request a wholesale account</h1>
              <p className="text-sm text-[#5C665A] mt-1">Tell us about your business. Accounts require admin approval before first sign-in.</p>
            </div>
            <RequiredLegend className="mt-2" />
          </div>
          <form onSubmit={submit} className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="register-form">
            <div>
              <FieldLabel required>First name</FieldLabel>
              <Input data-testid="reg-first" required value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className="h-10" />
            </div>
            <div>
              <FieldLabel required>Last name</FieldLabel>
              <Input data-testid="reg-last" required value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className="h-10" />
            </div>
            <div>
              <FieldLabel required>Email</FieldLabel>
              <Input data-testid="reg-email" required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="h-10" />
            </div>
            <div>
              <FieldLabel required>Password</FieldLabel>
              <Input data-testid="reg-password" required type="password" minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)} className="h-10" />
            </div>
            <div>
              <FieldLabel>Phone</FieldLabel>
              <Input data-testid="reg-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="h-10" />
            </div>
            <div>
              <FieldLabel required>Country</FieldLabel>
              <CountryCombobox
                value={form.country_code}
                onChange={(v) => set("country_code", v)}
                placeholder="Select country"
                testid="reg-country"
              />
            </div>
            <div className="sm:col-span-2">
              <FieldLabel required>Company</FieldLabel>
              <Input data-testid="reg-company" required value={form.company} onChange={(e) => set("company", e.target.value)} className="h-10" />
            </div>
            <div>
              <FieldLabel required>VAT / Tax ID</FieldLabel>
              <Input data-testid="reg-vat" required value={form.vat_id} onChange={(e) => set("vat_id", e.target.value)} className="h-10" />
            </div>
            <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <FieldLabel>Street address</FieldLabel>
                <AddressAutocomplete
                  testid="reg-street"
                  value={form.street_address}
                  onChange={(v) => set("street_address", v)}
                  country={form.country_code}
                  placeholder="Start typing — pick a suggestion to auto-fill city/postal/country"
                  className="h-10"
                  onPicked={(a) => setMany({
                    street_address: a.street || form.street_address,
                    city: a.city || form.city,
                    postal_code: a.postal_code || form.postal_code,
                    country_code: a.country_code || form.country_code,
                  })}
                />
              </div>
              <div className="sm:col-span-2">
                <FieldLabel>Address line 2</FieldLabel>
                <Input data-testid="reg-address2" value={form.address_line2} onChange={(e) => set("address_line2", e.target.value)} className="h-10" />
              </div>
              <div>
                <FieldLabel>Postal code</FieldLabel>
                <Input data-testid="reg-postal" value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} className="h-10" />
              </div>
              <div>
                <FieldLabel>City</FieldLabel>
                <Input data-testid="reg-city" value={form.city} onChange={(e) => set("city", e.target.value)} className="h-10" />
              </div>
            </div>
            <div className="flex items-end gap-3 pb-2">
              <label className="inline-flex items-center gap-2 text-sm text-[#1A1D1A] cursor-pointer">
                <Checkbox data-testid="reg-import-license" checked={form.import_license} onCheckedChange={(v) => set("import_license", !!v)} />
                Holds import licence <span className="text-xs text-[#5C665A]">(optional)</span>
              </label>
            </div>
            {form.import_license && (
              <div className="sm:col-span-2">
                <FieldLabel>Import licence number</FieldLabel>
                <Input data-testid="reg-import-license-number" value={form.import_license_number} onChange={(e) => set("import_license_number", e.target.value)} className="h-10" />
              </div>
            )}
            <div className="sm:col-span-2 pt-1 border-t border-[#E2E4E0]">
              <label className="inline-flex items-start gap-2 text-sm text-[#1A1D1A] cursor-pointer pt-3" data-testid="reg-terms-wrap">
                <Checkbox
                  className="mt-0.5"
                  data-testid="reg-accept-terms"
                  checked={form.accept_terms}
                  onCheckedChange={(v) => set("accept_terms", !!v)}
                  required
                />
                <span className="leading-relaxed">
                  <span className="text-[#C94A4A] mr-1">*</span>
                  I have read and accept the{" "}
                  <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-[#1B4332] underline underline-offset-2 hover:no-underline" data-testid="reg-terms-link">
                    General Terms and Conditions
                  </Link>.
                </span>
              </label>
            </div>
            <div className="sm:col-span-2 pt-2 flex items-center justify-between">
              <Link to="/login" className="text-sm text-[#5C665A] hover:text-[#1A1D1A]" data-testid="link-signin">Already have an account?</Link>
              <Button data-testid="register-submit" disabled={busy || !form.accept_terms} type="submit" className="h-10 bg-[#2D6A4F] hover:bg-[#1B4332] text-white disabled:opacity-50">
                {busy ? "Submitting…" : "Submit for review"}
              </Button>
            </div>
          </form>
        </div>
      </div>
      <Toaster position="bottom-right" richColors closeButton />
    </div>
  );
}
